/**
 * Organization Roles Manager
 *
 * Enhanced admin UI for managing organization roles with:
 * - System role mapping (6 core roles)
 * - Role terminology customization
 * - Program access control per role
 * - Page permissions with view/edit granularity
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  CBORoleType,
  ROLE_DEFINITIONS,
  SYSTEM_ROLE_ACCESS_LEVELS,
} from '@/types/cboRoles';
import {
  OrganizationRole,
  VALID_SYSTEM_ROLES,
  COMMON_FIELD_WORKER_TERMS,
  DEFAULT_ROLE_TERMINOLOGY,
} from '@/types/roleTerminology';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Pencil,
  Plus,
  Save,
  Languages,
  Users,
  Shield,
  FolderKanban,
  FileText,
  Eye,
  Edit3,
  Trash2,
  Check,
  X,
  RefreshCw,
  AlertCircle,
  Info,
  Settings,
  KeyRound,
  RotateCcw,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { RoleProgramAccessDialog } from './RoleProgramAccessDialog';
import { RoleFunctionsDialog } from './RoleFunctionsDialog';

interface OrganizationRolesManagerProps {
  organizationId: number;
  organizationName: string;
  onConfigurePermissions?: (roleType: string) => void;
}

interface SystemRoleInfo {
  role_key: string;
  display_name: string;
  display_name_es?: string;
  description?: string;
  access_level: number;
  can_be_restricted: boolean;
  is_admin_role: boolean;
}

export function OrganizationRolesManager({
  organizationId,
  organizationName,
  onConfigurePermissions,
}: OrganizationRolesManagerProps) {
  const { t, language } = useAppTranslation();

  // State
  const [roles, setRoles] = useState<OrganizationRole[]>([]);
  // Deactivated roles. Listed so they can be restored: the deactivate dialog
  // promises "It can be reactivated later", and re-creating the role 409s.
  // Until TRUS-663 the only way back was a bug that reactivated them on read.
  const [removedRoles, setRemovedRoles] = useState<OrganizationRole[]>([]);
  const [systemRoles, setSystemRoles] = useState<SystemRoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<OrganizationRole | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [programAccessRole, setProgramAccessRole] = useState<OrganizationRole | null>(null);
  const [functionsRole, setFunctionsRole] = useState<OrganizationRole | null>(null);

  // Form state - matches OrganizationRoleCreate schema
  const [formData, setFormData] = useState({
    role_type: '',
    display_name_en: '',
    display_name_es: '',
    description_en: '',
    description_es: '',
    system_role: 'staff' as CBORoleType,
    abbreviation: '',
    icon: '',
    color: '',
    is_field_worker: false,
    is_active: true,
    sort_order: 50,
  });

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [rolesRes, systemRolesRes] = await Promise.all([
        apiClient.get(`/api/organizations/${organizationId}/roles`, { params: { active_only: false } }),
        apiClient.get('/api/system-roles'),
      ]);

      const all: OrganizationRole[] = rolesRes.data || [];
      setRoles(all.filter((r) => r.is_active !== false));
      setRemovedRoles(all.filter((r) => r.is_active === false));
      setSystemRoles(systemRolesRes.data);
    } catch (err: any) {
      console.error('Error fetching roles:', err);
      setError(err.response?.data?.detail || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Handle edit
  const handleEdit = (role: OrganizationRole) => {
    setFormData({
      role_type: role.role_type,
      display_name_en: role.display_name_en,
      display_name_es: role.display_name_es || '',
      description_en: role.description_en || '',
      description_es: role.description_es || '',
      system_role: role.system_role,
      abbreviation: role.abbreviation || '',
      icon: role.icon || '',
      color: role.color || '',
      is_field_worker: role.is_field_worker,
      is_active: role.is_active,
      sort_order: role.sort_order,
    });
    setEditingRole(role);
    setIsCreating(false);
  };

  // Handle create
  const handleCreate = () => {
    setFormData({
      role_type: '',
      display_name_en: '',
      display_name_es: '',
      description_en: '',
      description_es: '',
      system_role: 'staff',
      abbreviation: '',
      icon: '',
      color: '',
      is_field_worker: false,
      is_active: true,
      sort_order: 50,
    });
    setEditingRole(null);
    setIsCreating(true);
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isCreating) {
        await apiClient.post(`/api/organizations/${organizationId}/roles`, formData);
      } else if (editingRole) {
        await apiClient.put(`/api/organizations/${organizationId}/roles/${editingRole.role_type}`, formData);
      }

      await fetchRoles();
      setEditingRole(null);
      setIsCreating(false);
    } catch (err: any) {
      console.error('Error saving role:', err);
      setError(err.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete (deactivate)
  const handleDelete = async (roleType: string, permanent: boolean = false) => {
    const action = permanent ? 'permanently delete' : 'deactivate';
    const warning = permanent
      ? 'This will PERMANENTLY delete the role and all associated permissions. This cannot be undone!'
      : 'This will deactivate the role. It can be reactivated later.';

    if (!confirm(`Are you sure you want to ${action} this role?\n\n${warning}`)) return;

    try {
      const url = permanent
        ? `/api/organizations/${organizationId}/roles/${roleType}/permanent`
        : `/api/organizations/${organizationId}/roles/${roleType}`;
      await apiClient.delete(url);
      await fetchRoles();
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error('Error deleting role:', err);
      const detail = err.response?.data?.detail || 'Failed to delete role';
      const status = err.response?.status;
      setError(`${detail}${status ? ` (${status})` : ''}`);
    }
  };

  const handleRestore = async (role: OrganizationRole) => {
    try {
      setError(null);
      await apiClient.put(`/api/organizations/${organizationId}/roles/${role.role_type}`, { is_active: true });
      await fetchRoles();
    } catch (err: any) {
      console.error('Error restoring role:', err);
      setError(err.response?.data?.detail || t('admin.roles_restore_failed', 'Failed to restore role'));
    }
  };

  // Quick select common terminology
  const handleQuickSelect = (term: { en: string; es: string; abbr: string; icon?: string; color?: string }) => {
    // Generate role_type from English name: lowercase, replace spaces with underscores
    const roleType = term.en.toLowerCase().replace(/\s+/g, '_');
    setFormData((prev) => ({
      ...prev,
      role_type: prev.role_type || roleType, // Only set if not already set
      display_name_en: term.en,
      display_name_es: term.es,
      abbreviation: term.abbr,
      icon: term.icon || prev.icon,
      color: term.color || prev.color,
    }));
  };

  // Get system role badge color
  const getSystemRoleBadgeColor = (systemRole: string) => {
    switch (systemRole) {
      case 'super_admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'org_admin':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'program_manager':
        return 'bg-violet-100 text-violet-800 border-violet-200';
      case 'supervisor':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'staff':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading roles...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Roles Reference */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Info className="w-6 h-6 text-blue-600" />
            <div>
              <CardTitle className="text-blue-900">System Roles (6 Core Permission Levels)</CardTitle>
              <CardDescription className="text-blue-700">
                Organization roles map to these system roles for permission inheritance.
                Admin roles (super_admin, org_admin) have full access and cannot be restricted.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {systemRoles.map((sr) => (
              <div
                key={sr.role_key}
                className={`p-3 rounded-lg border ${getSystemRoleBadgeColor(sr.role_key)}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{sr.display_name}</span>
                  <Badge variant="outline" className="text-xs">
                    L{sr.access_level}
                  </Badge>
                </div>
                <p className="text-xs opacity-75">{sr.description?.slice(0, 50)}...</p>
                {sr.is_admin_role && (
                  <Badge className="mt-2 text-xs bg-red-500">Admin</Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Organization Roles */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>Organization Roles</CardTitle>
                <CardDescription>
                  Custom role terminologies for {organizationName}. Each role maps to a system role for permissions.
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Role
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role Key</TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead>System Role</TableHead>
                <TableHead>Abbr</TableHead>
                <TableHead>Field Worker</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No roles configured. Click "Add Role" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-0.5 rounded">
                        {role.role_type}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="font-medium">{role.display_name_en}</span>
                        {role.display_name_es && (
                          <span className="text-sm text-muted-foreground ml-2">
                            ({role.display_name_es})
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getSystemRoleBadgeColor(role.system_role)}>
                        {role.system_role}
                      </Badge>
                      {role.access_level && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          L{role.access_level}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {role.abbreviation && (
                        <Badge variant="outline">{role.abbreviation}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {role.is_field_worker ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>{role.sort_order}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {onConfigurePermissions && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onConfigurePermissions(role.system_role || role.role_type)}
                            title="Configure page permissions for this role"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            <span className="text-xs">Permissions</span>
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setProgramAccessRole(role)}
                          title="Grant this role view/edit access to program members"
                        >
                          <FolderKanban className="w-4 h-4 mr-1" />
                          <span className="text-xs">Programs</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setFunctionsRole(role)}
                          title={t('admin.roleFunctions.buttonHint', "Grant this role specific actions, e.g. viewing their team's training records")}
                        >
                          <KeyRound className="w-4 h-4 mr-1" />
                          <span className="text-xs">{t('admin.roleFunctions.button', 'Functions')}</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(role)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDelete(role.role_type, false)}>
                              <X className="w-4 h-4 mr-2" />
                              Deactivate Role
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(role.role_type, true)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Permanently Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {removedRoles.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">
                {t('admin.roles_removed_title', 'Removed roles')}
              </h4>
              <p className="text-xs text-muted-foreground mb-2">
                {t('admin.roles_removed_hint', 'These roles cannot be assigned. Restore one to make it available again.')}
              </p>
              <div className="divide-y rounded-md border">
                {removedRoles.map((role) => (
                  <div key={role.id} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <code className="text-sm bg-muted px-2 py-0.5 rounded">{role.role_type}</code>
                      <span className="text-sm text-muted-foreground truncate">{role.display_name_en}</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleRestore(role)}>
                      <RotateCcw className="w-4 h-4 mr-1" />
                      <span className="text-xs">{t('admin.roles_restore', 'Restore')}</span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-program view/edit access for this role */}
      <RoleProgramAccessDialog
        organizationId={organizationId}
        role={programAccessRole}
        open={!!programAccessRole}
        onOpenChange={(o) => { if (!o) setProgramAccessRole(null); }}
      />

      {/* Per-function grants for this role (org_role_functions) */}
      <RoleFunctionsDialog
        organizationId={organizationId}
        role={functionsRole}
        open={!!functionsRole}
        onOpenChange={(o) => { if (!o) setFunctionsRole(null); }}
      />

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingRole || isCreating} onOpenChange={() => { setEditingRole(null); setIsCreating(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isCreating ? 'Create Organization Role' : 'Edit Organization Role'}
            </DialogTitle>
            <DialogDescription>
              Configure role terminology and system role mapping.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="advanced">Advanced Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              {/* Role Key */}
              <div>
                <Label htmlFor="role_type">Role Key</Label>
                <Input
                  id="role_type"
                  value={formData.role_type}
                  onChange={(e) => setFormData((prev) => ({ ...prev, role_type: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder="e.g., community_health_worker"
                  disabled={!!editingRole}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Unique identifier for this role (lowercase, underscores)
                </p>
              </div>

              {/* System Role */}
              <div>
                <Label htmlFor="system_role">System Role (Permission Level)</Label>
                <Select
                  value={formData.system_role}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, system_role: value as CBORoleType }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select system role" />
                  </SelectTrigger>
                  <SelectContent>
                    {VALID_SYSTEM_ROLES.map((sr) => (
                      <SelectItem key={sr} value={sr}>
                        <div className="flex items-center gap-2">
                          <Badge className={`${getSystemRoleBadgeColor(sr)} text-xs`}>{sr}</Badge>
                          <span className="text-sm">
                            {DEFAULT_ROLE_TERMINOLOGY[sr]?.en || sr}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  This determines the permission level for users with this role
                </p>
              </div>

              {/* Quick Select */}
              {formData.system_role === 'staff' && (
                <div>
                  <Label>Quick Select (Common Field Worker Terms)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {COMMON_FIELD_WORKER_TERMS.slice(0, 8).map((term, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickSelect(term)}
                        className={formData.display_name_en === term.en ? 'border-primary bg-primary/10' : ''}
                      >
                        {term.en}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Display Names */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="display_name_en">Display Name (English)</Label>
                  <Input
                    id="display_name_en"
                    value={formData.display_name_en}
                    onChange={(e) => setFormData((prev) => ({ ...prev, display_name_en: e.target.value }))}
                    placeholder="e.g., Community Health Worker"
                  />
                </div>
                <div>
                  <Label htmlFor="display_name_es">Display Name (Spanish)</Label>
                  <Input
                    id="display_name_es"
                    value={formData.display_name_es}
                    onChange={(e) => setFormData((prev) => ({ ...prev, display_name_es: e.target.value }))}
                    placeholder="e.g., Trabajador de Salud Comunitaria"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="description_en">Description (English)</Label>
                  <Textarea
                    id="description_en"
                    value={formData.description_en}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description_en: e.target.value }))}
                    placeholder="Role description..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="description_es">Description (Spanish)</Label>
                  <Textarea
                    id="description_es"
                    value={formData.description_es}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description_es: e.target.value }))}
                    placeholder="Descripción del rol..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Abbreviation */}
              <div>
                <Label htmlFor="abbreviation">Abbreviation</Label>
                <Input
                  id="abbreviation"
                  value={formData.abbreviation}
                  onChange={(e) => setFormData((prev) => ({ ...prev, abbreviation: e.target.value.toUpperCase() }))}
                  placeholder="e.g., CHW"
                  maxLength={5}
                  className="w-24"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Short code for compact views (max 5 chars)
                </p>
              </div>
            </TabsContent>

            <TabsContent value="advanced" className="space-y-4 mt-4">
              {/* Field Worker Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <Label>Field Worker Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Show simplified UI optimized for mobile/field work
                  </p>
                </div>
                <Switch
                  checked={formData.is_field_worker}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_field_worker: checked }))}
                />
              </div>

              {/* Sort Order */}
              <div>
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData((prev) => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Lower numbers appear first in role listings
                </p>
              </div>

              {/* Icon */}
              <div>
                <Label htmlFor="icon">Icon Name (Lucide)</Label>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
                  placeholder="e.g., UserHeart, Compass, Heart"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Lucide icon name (see lucide.dev for options)
                </p>
              </div>

              {/* Color */}
              <div>
                <Label htmlFor="color">Badge Color</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, color: value }))}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent>
                    {['green', 'blue', 'purple', 'pink', 'orange', 'amber', 'teal', 'indigo', 'red', 'gray'].map((color) => (
                      <SelectItem key={color} value={color}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full bg-${color}-500`} />
                          {color}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setEditingRole(null); setIsCreating(false); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !formData.role_type || !formData.display_name_en}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Preview</CardTitle>
          <CardDescription>
            How roles appear throughout the application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {roles.map((role) => (
              <div
                key={role.id}
                className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  {role.abbreviation && (
                    <Badge variant="outline">{role.abbreviation}</Badge>
                  )}
                  <Badge className={`${getSystemRoleBadgeColor(role.system_role)} text-xs`}>
                    {role.system_role}
                  </Badge>
                </div>
                <p className="font-medium">
                  {language === 'es' ? (role.display_name_es || role.display_name_en) : role.display_name_en}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {role.is_field_worker && (
                    <Badge variant="secondary" className="text-xs mr-1">Field</Badge>
                  )}
                  Order: {role.sort_order}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default OrganizationRolesManager;
